import merge from 'lodash/merge';
import { EventSystem } from '../../utils/eventSystem';
import { INACTIVITY_CONFIG } from './config';

export const InactivityEvents = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    NOTIFY: 'notify',
    TICK: 'tick',
};

class InactivityService {

    /**
     * The default configuration used for the InactivityService. These
     * will be merged with any config provided when initialized prioritizing
     * the config given to the service.
     */
    _DEFAULT_CONFIG = {
        /**
         * When set to `true`, activity logged by the service will be output to the
         * console.
         */
        debug: false,

        /**
         * How long to wait before determining the user has become inactive.
         */
        detectionDelay: 5000,

        /**
         * The interval on how often to trigger the tick event after the user has become
         * inactive. The tick event won't be fired while the user is considered to be
         * active.
         */
        inactivityTickInterval: 1000,

        /**
         * When to emit an inactivity notification. Note that this these times do not
         * include the initial detection delay. If the initial detection delay is 5000ms,
         * then the earliest a notification can be sent is after 5000ms.
         */
        notifyAfter: [
            5 * ( 1000 * 60 ), // 5 minute(s)
            30 * ( 1000 * 60 ), // 30 minute(s)
        ],

        watchKeyboard: true,
        watchMouse: true,
        watchBrowser: true,
    };

    _config = {};
    _isWatching = false;
    _inactivityDetectionTimer = null;
    _inactivityInterval = null;
    _durationInactive = 0;
    _notifyTimers = [];
    _eventSystem = null;

    constructor( config ) {
        // Merge the provided config with the service defaults.
        this._config = merge( {}, this._DEFAULT_CONFIG, config );

        // Initialize the event system for the service.
        this._eventSystem = new EventSystem();
    }

    /**
     * Returns whether or not the service is watching the user's interactions.
     * 
     * @returns { boolean }
     */
    isWatching = () => {
        return this._isWatching;
    }

    /**
     * Returns whether or not the user is considered to be inactive. If
     * the service is not currently watching the user, we will assume the
     * user is active, and not inactive.
     * 
     * @returns { boolean }
     */
    isInactive = () => {
        return !this.isActive();
    }

    /**
     * Returns whether or not the user is considered to be active. If the
     * service is not currently watching the user, we will assume the user
     * is active, and not inactive.
     * 
     * @returns { boolean }
     */
    isActive = () => {
        // Relies on the existence of the inactivity detection timer to
        // determine whether or not the user is inactive. If the timer
        // DOES exist, that means they are active and may become inactive
        // in the near future.
        return ( this._isWatching )
            ? ( this._inactivityDetectionTimer !== null )
            : true;
    }

    /**
     * Begins the service's watching of the user's interactions. While watching,
     * when an interaction is detected, the inactivity timer is reset. You can
     * configure what exactly the service watched through the config.
     * 
     * Returns whether or not the request to begin watching was successful.
     * 
     * @returns { boolean }
     */
    startWatching = () => {
        if ( !this._isWatching ) {
            this._isWatching = true;

            // If configured to watch the mouse interactions, register
            // interaction callback.
            if ( this._config?.watchMouse ) {
                window.addEventListener( 'mousemove', this._handleUserInteraction );
                window.addEventListener( 'scroll', this._handleUserInteraction );
                window.addEventListener( 'click', this._handleUserInteraction );
                // ...
            }

            // If configured to watch the keyboard interactions, register
            // interaction callback.
            if ( this._config?.watchKeyboard ) {
                window.addEventListener( 'keydown', this._handleUserInteraction );
                // ...
            }

            // If configured to watch the browser interactions, register
            // interaction callback.
            if ( this._config?.watchBrowser ) {
                window.addEventListener( 'resize', this._handleUserInteraction );
                window.addEventListener( 'focus', this._handleUserInteraction );
                // ...
            }

            // Start any timers used to track the user's inactivity.
            this._startInactivityDetectionTimer();

            return true;
        } else {
            console.warn( 'The Inactivity Service is already watching the user\'s interactions! The request to begin watching will be ignored.' );
            return false;
        }
    }

    /**
     * Stops the service from watching the user's interactions assuming it
     * was previously set to watch. Any user interactions that the service
     * was watching will no longer be detected.
     * 
     * Returns whether or not the request to stop watching was successful.
     * 
     * @returns { boolean }
     */
    stopWatching = () => {
        if ( this._isWatching ) {
            this._isWatching = false;

            // If configured to watch the mouse interactions, unregister
            // interaction callback.
            if ( this._config?.watchMouse ) {
                window.removeEventListener( 'mousemove', this._handleUserInteraction );
                window.removeEventListener( 'scroll', this._handleUserInteraction );
                window.removeEventListener( 'click', this._handleUserInteraction );
                // ...
            }

            // If configured to watch the keyboard interactions, unregister
            // interaction callback.
            if ( this._config?.watchKeyboard ) {
                window.removeEventListener( 'keydown', this._handleUserInteraction );
                // ...
            }

            // If configured to watch the browser interactions, unregister
            // interaction callback.
            if ( this._config?.watchBrowser ) {
                window.removeEventListener( 'resize', this._handleUserInteraction );
                window.removeEventListener( 'focus', this._handleUserInteraction );
                // ...
            }

            // Clear any timers being used to track the user's inactivity.
            this._clearInactivityDetectionTimer();
            this._clearNotifyTimers();

            return true;
        } else {
            console.warn( 'The Inactivity Service isn\'t currently watching the user\'s interactions! The request to stop watching will be ignored.' );
            return false;
        }
    }

    /**
     * Handles what happens when a user interaction is detected. Resets the
     * inactivity detection timer.
     */
    _handleUserInteraction = () => {
        // If the user has been considered to be inactive for any amount of
        // time, consider them now active.
        if ( this.isInactive() ) {
            this._handleUserActive();
        }

        this._startInactivityDetectionTimer();
    }

    /**
     * Starts the timer used to detect when the user is considered to be
     * inactive. If the timer had been previously set, that previous timer
     * will be cleared and a new one will be started.
     */
    _startInactivityDetectionTimer = ()=> {
        // If the inactivity timer was previously set, clear it and start
        // a new timer.
        this._clearInactivityDetectionTimer();
        this._inactivityDetectionTimer = setTimeout( this._handleUserInactive, this._config?.detectionDelay );
    }

    /**
     * Clears any existing timer used to track when the user will be considered
     * to be inactive.
     */
    _clearInactivityDetectionTimer = () => {
        if ( this._inactivityDetectionTimer ) {
            clearTimeout( this._inactivityDetectionTimer );
            this._inactivityDetectionTimer = null;
        }
    }

    /**
     * Starts the inactivity interval.
     */
    _startInactivityInterval = ()=> {
        // If the inactivity interval timer was previously set, clear it and
        // start a new timer.
        this._clearInactivityInterval();
        this._inactivityInterval = setInterval(
            this._handleInactivityTick,
            this._config.inactivityTickInterval,
        );
    }

    /**
     * Stops the inactivity interval. Any tracked time will be reset.
     */
    _clearInactivityInterval = () => {
        if ( this._inactivityInterval ) {
            clearInterval( this._inactivityInterval );
            this._inactivityInterval = null;
            this._durationInactive = 0;
        }
    }
    /**
     * Triggered at each inactivity interval when the user is considered to be
     * inactive.
     */
    _handleInactivityTick = () => {
        this._durationInactive += this._config.inactivityTickInterval;
        this._eventSystem.invoke( InactivityEvents.TICK, this._durationInactive );
    }

    /**
     * Handles what happens when a user becomes active after previously being
     * inactive.
     */
    _handleUserActive = () => {
        this._log( 'The user is now active!' );
        this._eventSystem.invoke( InactivityEvents.ACTIVE );

        this._clearNotifyTimers();
        this._clearInactivityInterval();
    }

    /**
     * After the user is considered to be inactive, queue up timers to handle
     * the notification callbacks.
     */
    _handleUserInactive = () => {
        this._log( 'The user is now inactive...' );
        this._eventSystem.invoke( InactivityEvents.INACTIVE );

        // Start the interval timer to track the time the user has been inactive.
        this._startInactivityInterval();

        // Remove the inactivity detection timer for now. The user has already been
        // considered to be inactive, so we don't need to track that any more. Also,
        // the existence of the timer is what we use to determine if the user is
        // active.
        this._inactivityDetectionTimer = null;

        const notificationDelays = this._config?.notifyAfter || [];
        for ( let i = 0; i < notificationDelays.length; i++ ) {
            this._notifyTimers.push( setTimeout( () => {
                this._handleNotication( i );
            }, notificationDelays[ i ] ) );
        }
    }

    /**
     * Clears all event timers used to notify that a notification marker has
     * been reached.
     */
    _clearNotifyTimers = () => {
        this._notifyTimers.forEach( ( notifyTimer ) => {
            clearTimeout( notifyTimer );
        } );
        this._notifyTimers = [];
    }

    /**
     * Handles the notification event.
     * 
     * @param { number } notificationIndex - The index of the notification being triggered.
     */
    _handleNotication = ( notificationIndex ) => {
        const notificationNumber = ( notificationIndex + 1 );
        const durationInactive = this._config.notifyAfter[ notificationIndex ];
        this._eventSystem.invoke( InactivityEvents.NOTIFY, notificationNumber, durationInactive );
    }

    /**
     * Registers a callback under an event.
     * 
     * @param { string } eventKey - The key of the event to register the callback under.
     * @param { function } callback - The callback to register under the event.
     */
    on = ( eventKey, callback ) => {
        this._eventSystem.addEventListener( eventKey, callback );
    }

    /**
     * Unregisters a callback from an event.
     * 
     * @param { string } eventKey - The key of the event to register the callback under.
     * @param { function } callback - The callback to unregister from the event.
     */
    off = ( eventKey, callback ) => {
        this._eventSystem.removeEventListener( eventKey, callback );
    }

    /**
     * Outputs a message to the console from the service if in debug mode. You
     * set the service into debug mode using the config provided to the service
     * during initialization.
     * 
     * @param { string } message - The message to log.
     */
    _log = ( message ) => {
        if ( this._config?.debug ) {
            console.log( `[ Inactivity Service ] ${ message }` );
        }
    }
}

export const inactivityService = new InactivityService( INACTIVITY_CONFIG );
